/**
 * A: Profitability - margin timeline, movers, league tables with
 * trend, actionable loss-makers and the margin distribution.
 * WiP ledger only. FGP (£) is the authoritative profit figure
 * (estimate on live projects, verified once finalised). Basis is
 * switchable: all, verified only (INVOICED/FINISHED), or live
 * estimates only.
 */
import { useMemo, useState } from "react";
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip, Cell, ReferenceArea,
} from "recharts";
import { fmtGbp, fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { useCfoData, TabState, KpiStrip, Card, SourceBadge, InfoTip } from "./cfoShared";
import { BASES } from "./explorerUtil";

const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const toQuarter = (ym) => `${ym.slice(0, 4)}-Q${Math.floor((+ym.slice(5, 7) - 1) / 3) + 1}`;

const fmtQuarter = (q) => `${q.slice(5)} ${q.slice(2, 4)}`;

const BAND_COLORS = ["#e11d48", "#d97706", "#a8a29e", "#5eead4", "#14b8a6", "#0f766e"];

const LEAGUE_INFO = {
  "prof-league-client": "prof_league_client",
  "prof-league-team": "prof_league_team",
  "prof-league-study": "prof_league_study",
};

const Trend = ({ pts }) => {
  if (pts == null) return <span className="text-stone-300">–</span>;
  return (
    <span className={pts >= 0 ? "text-teal-700" : "text-rose-600"}>
      {pts >= 0 ? "▲" : "▼"}{Math.abs(pts).toFixed(1)}
    </span>
  );
};

function League({ title, rows, scope, testid }) {
  const view = useMemo(() => {
    const mapped = rows.map((r) => ({
      ...r,
      _rev: scope === "ltm" ? r.ltm_revenue : r.revenue,
      _gp: scope === "ltm" ? r.ltm_gp : r.gp,
      _margin: scope === "ltm" ? r.ltm_margin_pct : r.margin_pct,
    })).filter((r) => r._rev > 0);
    return mapped.sort((a, b) => b._rev - a._rev);
  }, [rows, scope]);
  return (
    <Card testid={testid}>
      <PanelTitle>{title} <InfoTip id={LEAGUE_INFO[testid]} /></PanelTitle>
      <div className="max-h-80 overflow-y-auto pr-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100 sticky top-0 bg-white">
              <th className="py-1 text-left font-medium">&nbsp;</th>
              <th className="py-1 text-right font-medium">Revenue</th>
              <th className="py-1 text-right font-medium">Margin</th>
              <th className="py-1 text-right font-medium" title="LTM margin vs the 12 months before, in points">Trend</th>
            </tr>
          </thead>
          <tbody>
            {view.map((r) => (
              <tr key={r.key} className="border-b border-stone-50 last:border-0">
                <td className="py-1.5 font-medium text-slate-800 truncate max-w-[140px]">{r.label}</td>
                <td className="py-1.5 text-right font-mono tabular-nums text-stone-600">{fmtGbpShort(r._rev)}</td>
                <td className={`py-1.5 text-right font-mono tabular-nums ${r._margin != null && r._margin < 40 ? "text-rose-700" : "text-teal-700"}`}>
                  {r._margin != null ? `${r._margin}%` : "-"}
                </td>
                <td className="py-1.5 text-right font-mono tabular-nums text-[10px]">
                  <Trend pts={r.trend_pts} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LossRow({ p, live }) {
  return (
    <li className="py-2 flex items-center gap-3" data-testid={`prof-loss-${live ? "live" : "done"}-${p.cid}`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-800 truncate">
          {p.client} <span className="text-stone-400 font-normal">· {p.project_name}</span>
        </p>
        <p className="text-[10px] font-mono text-stone-400">
          {fmtGbp(p.revenue)} revenue · {p.status}
          {live && p.remaining > 0 && (
            <span className="text-amber-700"> · {fmtGbpShort(p.remaining)} still to invoice</span>
          )}
        </p>
      </div>
      {live && p.shortfall_45 > 0 && (
        <span className="text-[10px] font-mono tabular-nums text-stone-500 shrink-0" title="Profit short of the 45% benchmark">
          −{fmtGbpShort(p.shortfall_45)} vs 45%
        </span>
      )}
      <span className={`text-xs font-mono tabular-nums shrink-0 ${p.margin_pct < 0 ? "text-rose-700" : "text-amber-700"}`}>
        {p.margin_pct}%
      </span>
    </li>
  );
}

export default function ProfitabilityTab() {
  const [basis, setBasis] = useState("all");
  const [grain, setGrain] = useState("quarter");
  const [scope, setScope] = useState("ltm");
  const { data, loading, error } = useCfoData(`cfo/profitability?basis=${basis}`);
  const chart = useMemo(() => {
    const months = data?.months || [];
    if (grain === "month") {
      return months.map((m) => ({ ...m, period: m.month, label: fmtMonth(m.month) }));
    }
    const agg = new Map();
    for (const m of months) {
      const q = toQuarter(m.month);
      const a = agg.get(q) || {
        period: q, revenue: 0, gp: 0, known: 0, verified: 0, future: true,
      };
      a.revenue += m.revenue;
      a.gp += m.gp || 0;
      a.known += m.gp_coverage_pct != null ? (m.revenue * m.gp_coverage_pct) / 100 : 0;
      a.verified += m.verified_share_pct != null ? (m.revenue * m.verified_share_pct) / 100 : 0;
      if (!m.future) a.future = false;
      agg.set(q, a);
    }
    return [...agg.values()].map((a) => ({
      period: a.period,
      label: fmtQuarter(a.period),
      revenue: Math.round(a.revenue * 100) / 100,
      gp: Math.round(a.gp * 100) / 100,
      future: a.future,
      margin_pct: a.known > 0 ? Math.round((a.gp / a.known) * 100) : null,
      verified_share_pct: a.revenue > 0 ? Math.round((a.verified / a.revenue) * 100) : null,
    }));
  }, [data, grain]);
  const estimateBands = useMemo(() => {
    const bands = [];
    let start = null;
    chart.forEach((m, i) => {
      const est = m.margin_pct != null && (m.verified_share_pct == null || m.verified_share_pct < 50);
      if (est && start === null) start = m.label;
      if (!est && start !== null) {
        bands.push([start, chart[i - 1].label]);
        start = null;
      }
    });
    if (start !== null) bands.push([start, chart[chart.length - 1].label]);
    return bands;
  }, [chart]);

  const t = data?.totals || {};
  const marginTrend =
    t.margin_pct_12m != null && t.margin_pct_prev_12m != null
      ? Math.round((t.margin_pct_12m - t.margin_pct_prev_12m) * 10) / 10
      : null;

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-profitability">
      <Card testid="prof-basis-card">
        <div className="flex flex-wrap items-center gap-1" data-testid="prof-basis-toggle">
          <span className="text-[9px] font-mono uppercase tracking-wider text-stone-400 mr-1.5">
            Profit basis <InfoTip id="prof_basis" className="ml-0.5" /> <InfoTip id="prof_waterfall" className="ml-0.5" />
          </span>
          {BASES.map((b) => (
            <button
              key={b.id} type="button" onClick={() => setBasis(b.id)}
              data-testid={`prof-basis-${b.id}`}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                basis === b.id
                  ? "bg-[#1B4F8A] border-[#1B4F8A] text-white"
                  : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </Card>

      {(loading || error) && <TabState loading={loading} error={error} testid="cfo-profitability" />}

      {data && !loading && (
        <>
          <KpiStrip cards={[
            { label: "Revenue · since Jan 23", value: fmtGbpShort(t.revenue), tone: "text-teal-700", testid: "prof-kpi-revenue", info: "prof_revenue" },
            { label: "Gross profit · since Jan 23", value: fmtGbpShort(t.gp), tone: "text-slate-900", testid: "prof-kpi-gp", info: "prof_gp" },
            {
              label: "Margin · last 12 months", value: t.margin_pct_12m != null ? `${t.margin_pct_12m}%` : "-",
              tone: "text-[#1B4F8A]", testid: "prof-kpi-margin", info: "prof_margin",
              sub: `${marginTrend != null ? `${marginTrend >= 0 ? "▲" : "▼"} ${Math.abs(marginTrend)}pts vs prior 12m · ` : ""}all-time ${t.margin_pct != null ? `${t.margin_pct}%` : "-"} · profit known on ${t.gp_coverage_pct}%`,
              subTone: marginTrend != null && marginTrend < 0 ? "text-rose-600" : "text-teal-600",
            },
            {
              label: "1 margin point =", value: t.point_value_gbp != null ? `${fmtGbpShort(t.point_value_gbp)}/yr` : "-",
              tone: "text-slate-900", testid: "prof-kpi-point-value", info: "prof_point_value",
              sub: `at last-12-months volume (${fmtGbpShort(t.revenue_12m)})`,
            },
          ]} />

          <Card testid="prof-margin-chart">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <PanelTitle>Revenue &amp; margin per {grain === "quarter" ? "quarter" : "month"} <InfoTip id="prof_margin_chart" /></PanelTitle>
              <div className="flex items-center gap-3">
                <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono">
                  {[
                    { id: "quarter", label: "Quarterly" },
                    { id: "month", label: "Monthly" },
                  ].map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setGrain(o.id)}
                      data-testid={`prof-grain-${o.id}`}
                      className={`px-2.5 py-1 transition-colors ${grain === o.id ? "bg-[#1B4F8A] text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <SourceBadge kind="wip" />
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={grain === "quarter" ? 1 : 3} />
                  <YAxis yAxisId="rev" tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
                  <YAxis yAxisId="m" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={38} />
                  <RechartsTooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v, name) => name === "margin_pct" ? [`${v}%`, "margin"] : [fmtGbpShort(v), name === "revenue" ? "revenue" : "GP"]}
                    labelFormatter={(l, p) => {
                      const pl = p?.[0]?.payload;
                      if (!pl) return l;
                      const share = pl.verified_share_pct;
                      const base = pl.month ? fmtMonth(pl.month) : fmtQuarter(pl.period);
                      return `${base}${share != null ? ` · ${Math.round(share)}% verified` : ""}`;
                    }}
                  />
                  {estimateBands.map((b) => (
                    <ReferenceArea key={b[0]} yAxisId="rev" x1={b[0]} x2={b[1]} fill="#D97706" fillOpacity={0.08} />
                  ))}
                  <Bar yAxisId="rev" dataKey="revenue" maxBarSize={grain === "quarter" ? 26 : 16} radius={[2, 2, 0, 0]}>
                    {chart.map((m) => (
                      <Cell key={m.period} fill={m.future ? "#3EA8DC" : "#0D9488"} fillOpacity={m.future ? 0.5 : 1} />
                    ))}
                  </Bar>
                  <Line yAxisId="m" dataKey="margin_pct" stroke="#1B4F8A" strokeWidth={1.5} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-stone-400 mt-2">
              Lighter bars = scheduled {grain === "quarter" ? "quarters" : "months"} (predicted profit). Amber zones = {grain === "quarter" ? "quarters" : "months"} where most
              revenue sits on live-project estimates, not finalised accounts - the margin line
              there is expectation, not fact.
            </p>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card testid="prof-movers">
              <PanelTitle>Margin movers · LTM vs the 12 months before <InfoTip id="prof_movers" /></PanelTitle>
              {(data.movers || []).length === 0 ? (
                <p className="text-xs text-stone-400 py-2">Not enough two-window history yet.</p>
              ) : (
                <ul className="divide-y divide-stone-50 mt-1">
                  {data.movers.map((m) => (
                    <li key={`${m.kind}-${m.label}`} className="py-2 flex items-center gap-3" data-testid={`prof-mover-${m.label}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {m.label}
                          <span className="ml-1.5 text-[9px] font-mono uppercase tracking-wider text-stone-400 border border-stone-200 rounded px-1 py-px">{m.kind}</span>
                        </p>
                        <p className="text-[10px] font-mono text-stone-400">
                          {m.prev_margin_pct}% → {m.ltm_margin_pct}% · {fmtGbpShort(m.ltm_revenue)} LTM revenue
                        </p>
                      </div>
                      <span className="text-xs font-mono tabular-nums shrink-0"><Trend pts={m.pts} />pts</span>
                      <span className={`text-xs font-mono tabular-nums shrink-0 w-20 text-right ${m.impact_gbp >= 0 ? "text-teal-700" : "text-rose-600"}`}>
                        {m.impact_gbp >= 0 ? "+" : "−"}{fmtGbpShort(Math.abs(m.impact_gbp))}/yr
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card testid="prof-bands">
              <PanelTitle>Margin distribution · projects by band <InfoTip id="prof_bands" /></PanelTitle>
              <div className="h-56 mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.margin_bands || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="band" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={34} />
                    <RechartsTooltip
                      contentStyle={{ fontSize: 11, borderRadius: 8 }}
                      formatter={(v, n, p) => [`${v} projects · ${fmtGbpShort(p?.payload?.revenue)} revenue`, p?.payload?.band]}
                    />
                    <Bar dataKey="count" maxBarSize={44} radius={[3, 3, 0, 0]}>
                      {(data.margin_bands || []).map((b, i) => (
                        <Cell key={b.band} fill={BAND_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-stone-400 mt-2">
                Every project with at least £500 of profit-known revenue, under the chosen basis.
              </p>
            </Card>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">League tables</p>
            <div className="flex rounded-lg border border-stone-200 overflow-hidden text-[10px] font-mono bg-white" data-testid="prof-scope-toggle">
              {[
                { id: "ltm", label: "Last 12 months" },
                { id: "all", label: "Since Jan 23" },
              ].map((o) => (
                <button
                  key={o.id} type="button" onClick={() => setScope(o.id)}
                  data-testid={`prof-scope-${o.id}`}
                  className={`px-2.5 py-1 transition-colors ${scope === o.id ? "bg-[#1B4F8A] text-white" : "text-stone-500 hover:bg-stone-50"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <League title="Margin by client" rows={data.by_client} scope={scope} testid="prof-league-client" />
            <League title="Margin by team" rows={data.by_team} scope={scope} testid="prof-league-team" />
            <League title="Margin by study type" rows={data.by_study_type} scope={scope} testid="prof-league-study" />
          </div>

          <Card testid="prof-loss-makers">
            <PanelTitle>Live projects below 25% · profit still protectable <InfoTip id="prof_loss" /></PanelTitle>
            {(data.loss_live || []).length === 0 ? (
              <p className="text-xs text-stone-400 py-2">No live project sits below the 25% line.</p>
            ) : (
              <ul className="divide-y divide-stone-50">
                {data.loss_live.map((p) => (
                  <LossRow key={p.cid || p.project_name} p={p} live />
                ))}
              </ul>
            )}
            {(data.loss_finished || []).length > 0 && (
              <details className="mt-3" data-testid="prof-loss-finished">
                <summary className="text-[11px] font-mono text-stone-500 cursor-pointer select-none hover:text-slate-800">
                  Finished projects below 25% · lessons ({data.loss_finished.length})
                </summary>
                <ul className="divide-y divide-stone-50 mt-1">
                  {data.loss_finished.map((p) => (
                    <LossRow key={p.cid || p.project_name} p={p} live={false} />
                  ))}
                </ul>
              </details>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
